
import { Col, Input, Pagination, Row, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import { useGetTrackNTraceCountsByUserQuery } from "../../../redux/services/trackntraceApiSlice";
import { removeEmptyKeys } from "../../../utils";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { UITable } from "../../common/table";
import ReportFiltersCard from "./ReportFiltersCard";
import { REPORT_TABLE_BODY_MAX_PX } from "./reportTableLayout";
import "./style.css";

type TrackTraceRow = {
  key: string;
  tagId: string;
  epc: string;
  siteName: string;
  zone: string;
  transferredFromLocation: string;
  transferredToLocation: string;
  lastUpdatedAt: string;
};

const TrackTraceReport = () => {
  const [searchParams] = useSearchParams();
  const identityId = searchParams.get("identityId") ?? "";
  const sku = searchParams.get("sku") ?? "";
  const asnParam = searchParams.get("asn") ?? "";
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const source = searchParams.get("source") ?? "";
  const tenantId = useSelector(getTenantId);
  const { zoneRows, siteNameById, zoneNameById, siteOptionsById, zoneOptionsById } = useTenantSitesAndZones(tenantId);

  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [zoneId, setZoneId] = useState<string | undefined>(undefined);
  const [tagIdFilter, setTagIdFilter] = useState("");
  const [epcFilter, setEpcFilter] = useState("");
  const [appliedSiteId, setAppliedSiteId] = useState<string | undefined>(undefined);
  const [appliedZoneId, setAppliedZoneId] = useState<string | undefined>(undefined);
  const [appliedTagId, setAppliedTagId] = useState("");
  const [appliedEpc, setAppliedEpc] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState<{ field?: keyof TrackTraceRow; order?: "ascend" | "descend" }>({});

  const queryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        reportType: source === "receive" ? "receive" : "transfer",
        page,
        limit: pageSize,
        sku: sku || undefined,
        asn: asnParam.trim() || undefined,
        identityId: identityId || undefined,
        from: fromParam || undefined,
        to: toParam || undefined,
        zone: appliedZoneId ? zoneNameById.get(appliedZoneId) || undefined : undefined,
        siteName: appliedSiteId ? siteNameById.get(appliedSiteId) || undefined : undefined,
        tagIdFilter: appliedTagId.trim() || undefined,
        tagId: appliedTagId.trim() || identityId || undefined,
        epc: appliedEpc.trim() || undefined,
      }),
    [
      tenantId,
      source,
      page,
      pageSize,
      sku,
      asnParam,
      identityId,
      fromParam,
      toParam,
      appliedSiteId,
      appliedZoneId,
      siteNameById,
      zoneNameById,
      appliedTagId,
      appliedEpc,
    ]
  );

  const { data, isFetching } = useGetTrackNTraceCountsByUserQuery(queryParams, {
    skip: !tenantId,
  });

  const result = useMemo(() => data?.result ?? data, [data]);
  const rawRows = useMemo(() => result?.rows ?? result?.data ?? [], [result]);
  const totalCount = Number(result?.totalCount ?? result?.total ?? result?.count ?? 0);

  // [LOGIC] Memoize data transformation from raw API response to table-ready rows.
  // Handles various ID formats (diId, epc, primaryId) to ensure consistency across different tag versions.
  const rows = useMemo<TrackTraceRow[]>(() => {
    if (!Array.isArray(rawRows)) return [];
    return rawRows.map((row: Record<string, unknown>, index: number) => {
      const recentSites = Array.isArray(row.recentSites)
        ? (row.recentSites as Array<Record<string, unknown>>)
        : [];
      const latestSite = recentSites[0];
      return {
        key: String(row.id ?? row._id ?? row.tagId ?? row.primaryId ?? row.diId ?? row.epc ?? `${index}`),
        tagId: String(row.tagId ?? row.primaryId ?? row.diId ?? row.identityId ?? ""),
        epc: String(row.epc ?? row.tagId ?? row.primaryId ?? row.diId ?? ""),
        siteName: String(row.siteName ?? latestSite?.siteName ?? ""),
        zone: String(row.zone ?? row.zoneName ?? latestSite?.zoneName ?? ""),
        transferredFromLocation: String(
          row.transferredFromLocation ?? row.transferredFrom ?? row.fromLocation ?? row.sourceLocation ?? ""
        ),
        transferredToLocation: String(
          row.transferredToLocation ?? row.transferredTo ?? row.toLocation ?? row.destinationLocation ?? ""
        ),
        lastUpdatedAt: String(row.lastUpdatedAt ?? row.operationTime ?? row.createdAt ?? latestSite?.operationTime ?? ""),
      };
    });
  }, [rawRows]);

  const filteredRows = useMemo(() => {
    const tagIdQuery = appliedTagId.trim().toLowerCase();
    const epcQuery = appliedEpc.trim().toLowerCase();
    const appliedSiteName = appliedSiteId ? siteNameById.get(appliedSiteId) : "";
    const appliedZoneName = appliedZoneId ? zoneNameById.get(appliedZoneId) : "";

    return rows.filter((row) => {
      const siteMatch = !appliedSiteName || row.siteName === appliedSiteName;
      const zoneMatch = !appliedZoneName || row.zone === appliedZoneName;
      const tagIdMatch =
        !tagIdQuery || row.tagId.toLowerCase().includes(tagIdQuery);
      const epcMatch = !epcQuery || row.epc.toLowerCase().includes(epcQuery);

      return siteMatch && zoneMatch && tagIdMatch && epcMatch;
    });
  }, [rows, appliedSiteId, appliedZoneId, siteNameById, zoneNameById, appliedTagId, appliedEpc]);
  const sortedRows = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return filteredRows;
    const dir = sortConfig.order === "ascend" ? 1 : -1;
    const field = sortConfig.field;
    return [...filteredRows].sort((a, b) => {
      if (field === "lastUpdatedAt") return (moment(a.lastUpdatedAt).valueOf() - moment(b.lastUpdatedAt).valueOf()) * dir;
      return String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * dir;
    });
  }, [filteredRows, sortConfig]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const applyFilters = () => {
    setAppliedSiteId(siteId);
    setAppliedZoneId(zoneId);
    setAppliedTagId(tagIdFilter.trim());
    setAppliedEpc(epcFilter);
    setPage(1);
  };

  const resetFilters = () => {
    setSiteId(undefined);
    setZoneId(undefined);
    setTagIdFilter("");
    setEpcFilter("");
    setAppliedSiteId(undefined);
    setAppliedZoneId(undefined);
    setAppliedTagId("");
    setAppliedEpc("");
    setPage(1);
    setPageSize(50);
  };

  const initialColumns: ColumnsType<TrackTraceRow> = [
    { title: "Tag ID", dataIndex: "tagId", key: "tagId", width: 160, sorter: true },
    { title: "EPC", dataIndex: "epc", key: "epc", width: 180, sorter: true },
    { title: "Zone", dataIndex: "zone", key: "zone", width: 120, sorter: true },
    {
      title: "Transferred From Location",
      dataIndex: "transferredFromLocation",
      key: "transferredFromLocation",
      width: 190,
      sorter: true,
    },
    {
      title: "Transferred To Location",
      dataIndex: "transferredToLocation",
      key: "transferredToLocation",
      width: 190,
      sorter: true,
    },
    {
      title: "Last Updated At",
      dataIndex: "lastUpdatedAt",
      key: "lastUpdatedAt",
      width: 180,
      sorter: true,
      render: (value: string) => (value ? moment(value).format("DD MMM YY hh:mm A") : "-"),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TrackTraceRow> | SorterResult<TrackTraceRow>[]
  ) => {
    setPage(newPagination.current ?? 1);
    setPageSize(newPagination.pageSize ?? 50);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortConfig({
      field: (s?.field as keyof TrackTraceRow | undefined) ?? undefined,
      order: (s?.order as "ascend" | "descend" | undefined) ?? undefined,
    });
  };

  const exportCsv = () => {
    const header = [
      "Tag ID",
      "EPC",
      "Zone",
      "Transferred From Location",
      "Transferred To Location",
      "Last Updated At",
    ];
    const csvContent = [
      header.join(","),
      ...filteredRows.map((row) =>
        [
          row.tagId,
          row.epc,
          row.zone,
          row.transferredFromLocation,
          row.transferredToLocation,
          row.lastUpdatedAt ? moment(row.lastUpdatedAt).format("DD MMM YY hh:mm A") : "",
        ]
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "track-trace-report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const header = [
      "Tag ID",
      "EPC",
      "Zone",
      "Transferred From Location",
      "Transferred To Location",
      "Last Updated At",
    ];
    const tsvContent = [
      header.join("\t"),
      ...filteredRows.map((row) =>
        [
          row.tagId,
          row.epc,
          row.zone,
          row.transferredFromLocation,
          row.transferredToLocation,
          row.lastUpdatedAt ? moment(row.lastUpdatedAt).format("DD MMM YY hh:mm A") : "",
        ].join("\t")
      ),
    ].join("\n");
    const blob = new Blob(["\ufeff" + tsvContent], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "track-trace-report.xls";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="di-reports-page track-trace-page inventory-reports-page">
      <ReportFiltersCard>
        <Row gutter={[12, 12]} align="bottom" className="filters-row">
          <Col className="filter-field-col">
            <span className="filter-label">Site</span>
            <Select
              placeholder="Site"
              value={siteId}
              onChange={(value) => {
                setSiteId(value);
                setZoneId(undefined);
              }}
              allowClear
              className="filter-input-control filter-select"
              options={siteOptionsById}
            />
          </Col>
          <Col className="filter-field-col">
            <span className="filter-label">Zone</span>
            <Select
              placeholder="Zone"
              value={zoneId}
              onChange={(value) => {
                setZoneId(value);
                const z = zoneRows.find((r) => String(r.id) === String(value));
                if (z?.siteId) setSiteId(String(z.siteId));
              }}
              allowClear
              className="filter-input-control filter-select"
              options={zoneOptionsById}
            />
          </Col>
          <Col className="filter-field-col">
            <span className="filter-label">Tag ID</span>
            <Input
              placeholder="Tag ID"
              value={tagIdFilter}
              onChange={(e) => setTagIdFilter(e.target.value)}
              allowClear
              className="filter-input-control"
            />
          </Col>
          <Col className="filter-field-col">
            <span className="filter-label">EPC</span>
            <Input
              placeholder="EPC"
              value={epcFilter}
              onChange={(e) => setEpcFilter(e.target.value)}
              allowClear
              className="filter-input-control"
            />
          </Col>
          <Col className="filters-inline-actions">
            <button type="button" className="btn-apply-filters" onClick={applyFilters}>
              Apply Filters
            </button>
            <button type="button" className="btn-reset-filters" onClick={resetFilters}>
              Reset
            </button>
          </Col>
        </Row>
      </ReportFiltersCard>

      <section className="enablement-table-section">
        <div className="table-card">
          <UITable
            rowKey="key"
            columns={columns}
            setColumns={setColumns}
            data={pagedRows}
            pagination={false}
            loading={isFetching}
            handleTableChange={handleTableChange}
            scroll={{ x: 1250, y: REPORT_TABLE_BODY_MAX_PX }}
            className="DITable diInfo"
          />
          <div className="reports-table-footer">
            <div className="enablement-pagination-actions">
              <button type="button" className="report-action-btn" onClick={exportCsv}>
                Export CSV
              </button>
              <button type="button" className="report-action-btn" onClick={exportExcel}>
                Export Excel
              </button>
            </div>
            <div className="pagination-with-size">
              <Pagination
                current={page}
                defaultCurrent={1}
                pageSize={pageSize}
                defaultPageSize={25}
                total={filteredRows.length || totalCount}
                hideOnSinglePage={false}
                showSizeChanger={false}
                onChange={(newPage) => {
                  setPage(newPage);
                }}
              />
              <select
                className="pagination-size-select-native"
                value={String(pageSize)}
                onChange={(event) => {
                  setPage(1);
                  setPageSize(Number(event.target.value));
                }}
              >
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrackTraceReport;
